import { listApiData } from "@hooks/utils/api";
import { catchSentryError } from "@utils/sentry";
import { useCallback, useEffect, useState } from "react";
import {
  decodeOrganisationFrameworkSummary,
  OrganisationFrameworkSummary
} from "catchment-common/decoders/projectFramework";
import { OrganisationFrameworkSummaryLineItem } from "catchment-common/interfaces/projectFramework";

/**
 * Fired by anything that changes which frameworks the organisation's projects
 * hold WITHOUT a route transition — archiving a project, today. The sidebar's
 * instance of this hook listens and re-reads; the same window-event
 * arrangement `utils/authErrors` uses for session expiry.
 */
export const ORGANISATION_FRAMEWORKS_STALE_EVENT =
  "organisation-frameworks-stale";

export const notifyOrganisationFrameworksStale = (): void => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(ORGANISATION_FRAMEWORKS_STALE_EVENT));
  }
};

/**
 * Every framework the organisation's projects report against — catalogue and
 * org-authored alike — one row per framework id with how many projects hold
 * it. What the org sidebar's Compliance rows and the org Compliance rollup
 * list; a template nothing has adopted is the Library's to show, not this
 * hook's.
 */
export const useOrganisationFrameworks = (organisationId?: string) => {
  const [frameworks, setFrameworks] = useState<
    OrganisationFrameworkSummaryLineItem[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /**
   * Whether a list has ever arrived — which is a different question from
   * `isLoading`, and the one the sidebar needs.
   *
   * The sidebar holds its whole menu back until the Compliance rows are known,
   * so the list does not appear and then grow. `isLoading` cannot express that
   * on its own: it is FALSE on the first render, before the organisation id
   * exists to fetch with, so a menu gated on it renders, hides and renders
   * again — a worse flicker than the one being fixed.
   *
   * Never reset once true. A later `refresh()` — leaving project set-up, a
   * project being archived — updates the rows in place, and blanking the
   * sidebar on every such navigation is exactly the jump this is here to
   * prevent.
   */
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const listOrganisationFrameworks = useCallback(
    async (orgId: string): Promise<void> => {
      try {
        setIsLoading(true);
        const response = await listApiData(
          "listOrganisationFrameworks",
          // Routed through the frameworkTemplate handler, which owns the
          // org-scoped framework reads and the membership check they share.
          "frameworkTemplate",
          orgId
        );
        setFrameworks(
          (response.data as unknown as OrganisationFrameworkSummary[]).map(
            decodeOrganisationFrameworkSummary
          )
        );
      } catch (e) {
        catchSentryError(e);
        setError("Could not list organisation frameworks");
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
      }
    },
    []
  );

  useEffect(() => {
    if (organisationId) {
      listOrganisationFrameworks(organisationId);
    }
  }, [organisationId, listOrganisationFrameworks]);

  /** Re-reads the list. For callers that know the world changed — the org
   * sidebar after a project set-up save — since the mount-time fetch cannot. */
  const refresh = useCallback(() => {
    if (organisationId) {
      listOrganisationFrameworks(organisationId);
    }
  }, [organisationId, listOrganisationFrameworks]);

  useEffect(() => {
    window.addEventListener(ORGANISATION_FRAMEWORKS_STALE_EVENT, refresh);
    return () =>
      window.removeEventListener(ORGANISATION_FRAMEWORKS_STALE_EVENT, refresh);
  }, [refresh]);

  return { error, frameworks, hasLoaded, isLoading, refresh };
};
